import type {
  TGetExperienceResponsePayload,
  TMessageInfo,
  TSetUserInputsRequestPayload,
} from '@cascade/api-client/ge-v2';
import { areArraysEqual } from '@cascade/core/utils';
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLatest } from '#hooks/use-latest/index.ts';
import { useApi } from '#providers/api/index.ts';
import { useProjects } from '#providers/projects/index.ts';
import { createQueryKey, getQueryLoadState } from '#providers/query/index.ts';
import {
  ExperienceAnswersContext,
  ExperienceContext,
} from './Experience.context.ts';
import * as helpers from './Experience.helpers.ts';
import { useExperienceState } from './use-experience-state/index.ts';
import type {
  TAnswer,
  TAnswers,
  TExperienceAnswersContextValue,
  TExperienceContextValue,
  TExperienceProviderProps,
  TPendingSave,
} from './Experience.types.ts';

const ExperienceContextProvider = ({
  experienceId,
  isMobile,
  isStepHidden,
  children,
}: TExperienceProviderProps) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { currentProjectId, tagValues, requireProjectId } = useProjects();
  const projectId = currentProjectId ?? '';

  const { state, dispatch } = useExperienceState(projectId, experienceId);
  const pendingSaveRef = useRef<TPendingSave>(undefined);
  const stateRef = useLatest(state);

  const { visitedStepIds, answers } = state;

  const { experienceRequest, experienceKey } = useMemo(
    () => helpers.getExperienceQuery(projectId, experienceId),
    [projectId, experienceId],
  );

  const experienceQuery = useQuery({
    queryKey: experienceKey,
    queryFn: currentProjectId
      ? () => api.geV2.getExperienceV2(experienceRequest)
      : skipToken,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data: experience, refetch: refetchExperience } = experienceQuery;
  const steps = experience?.steps;
  const { isLoading: isLoadingExperience, loadError: experienceError } =
    getQueryLoadState(experienceQuery);

  const isConsentRequired =
    Boolean(experience?.requires_consent) &&
    Boolean(experience?.consent_text) &&
    !experience?.has_consented;

  const mainSteps = useMemo(() => helpers.getMainSteps(steps), [steps]);
  const currentStepId = helpers.getCurrentStepId(visitedStepIds, mainSteps);
  const { stepRequest, stepKey, contentRequest, contentKey } =
    helpers.getStepQueries(
      projectId,
      experienceId,
      currentStepId ?? '',
      tagValues,
      isMobile,
    );

  const stepQuery = useQuery({
    queryKey: stepKey,
    queryFn:
      currentStepId && !isConsentRequired && !isStepHidden
        ? () => api.geV2.getStepV2(stepRequest)
        : skipToken,
  });

  const { data: loadedStep, refetch: refetchStep } = stepQuery;
  const { isLoading: isLoadingStep, loadError: stepError } =
    getQueryLoadState(stepQuery);

  const answeredHiddenWidgetIds = loadedStep
    ? helpers.getHiddenWidgetIds(loadedStep, answers)
    : [];

  const [hiddenWidgetIds, setHiddenWidgetIds] = useState(
    answeredHiddenWidgetIds,
  );

  if (!areArraysEqual(hiddenWidgetIds, answeredHiddenWidgetIds)) {
    setHiddenWidgetIds(answeredHiddenWidgetIds);
  }

  const visibleStep = useMemo(
    () =>
      loadedStep
        ? helpers.removeHiddenWidgets(loadedStep, hiddenWidgetIds)
        : undefined,
    [loadedStep, hiddenWidgetIds],
  );

  const isStepPending = visibleStep
    ? helpers.hasPendingWidgets(visibleStep)
    : false;

  const contentQuery = useQuery({
    queryKey: contentKey,
    queryFn:
      isStepPending && !isStepHidden
        ? () => api.geV2.getAiContentV2(contentRequest)
        : skipToken,
    retry: false,
    staleTime: Infinity,
  });

  const { data: generatedContent, refetch: refetchContent } = contentQuery;
  const isGeneratingContent = contentQuery.isFetching;

  const answersMutation = useMutation({
    mutationFn: (userInputs: TAnswers) =>
      api.geV2.setUserInputsV2({
        project_id: requireProjectId(),
        experience_id: experienceId,
        is_mobile: isMobile,
        user_inputs: userInputs as TSetUserInputsRequestPayload['user_inputs'],
      }),
    onSuccess: (_result, userInputs) => {
      dispatch({ type: 'answers.saved', answers: userInputs });

      const otherDynamicSteps = (steps ?? []).filter(
        (step) => step.has_dynamic_content && step.id !== currentStepId,
      );

      otherDynamicSteps.forEach((step) => {
        const otherQueries = helpers.getStepQueries(
          projectId,
          experienceId,
          step.id,
          tagValues,
          isMobile,
        );

        const stepKeyForAnyTags = createQueryKey(
          'geV2',
          'getStepV2',
          otherQueries.stepRequest,
        );

        queryClient.removeQueries({ queryKey: otherQueries.contentKey });
        queryClient.invalidateQueries({ queryKey: stepKeyForAnyTags });
      });
    },
  });

  const { mutateAsync: sendAnswers } = answersMutation;
  const isSavingAnswers = answersMutation.isPending;
  const saveAnswersError = answersMutation.error ?? undefined;

  const consentMutation = useMutation({
    mutationFn: () =>
      api.geV2.acceptConsent({
        project_id: requireProjectId(),
        experience_id: experienceId,
      }),
    onSuccess: (consent) => {
      queryClient.setQueryData<TGetExperienceResponsePayload>(
        experienceKey,
        (cached) =>
          cached && {
            ...cached,
            has_consented: consent.consent_accepted,
            consent_accepted_at: consent.consent_accepted_at,
          },
      );
    },
  });

  const { mutateAsync: acceptConsent } = consentMutation;
  const isAcceptingConsent = consentMutation.isPending;
  const acceptConsentError = consentMutation.error ?? undefined;

  const currentStep = useMemo(() => {
    if (!visibleStep) {
      return undefined;
    }

    const generatedStep = generatedContent
      ? helpers.applyGeneratedContent(visibleStep, generatedContent)
      : visibleStep;

    return helpers.sortStep(generatedStep);
  }, [visibleStep, generatedContent]);

  const generateContentError =
    contentQuery.isLoadingError && !isGeneratingContent && isStepPending
      ? contentQuery.error
      : undefined;

  const previousStepId = helpers.getPreviousStepId(
    visitedStepIds,
    mainSteps,
    currentStepId,
  );

  const nextStepId = helpers.getNextStepId(mainSteps, currentStepId);
  const isSideStep =
    currentStepId !== undefined &&
    !helpers.isMainStepId(mainSteps, currentStepId);

  const activeMainStepId = isSideStep ? previousStepId : currentStepId;
  const isLastStep =
    currentStepId !== undefined && currentStepId === mainSteps.at(-1)?.id;

  const canAnswer = !isSavingAnswers && !isGeneratingContent;
  const isStepBusy = isLoadingStep || !canAnswer;

  const completedStepIds = useMemo(
    () => helpers.getCompletedStepIds(visitedStepIds, currentStepId),
    [visitedStepIds, currentStepId],
  );

  const saveAnswers = useCallback((): Promise<void> => {
    const { answers: answersToSave, hasUnsavedAnswers } = stateRef.current;
    const pendingSave = pendingSaveRef.current;

    if (
      pendingSave &&
      helpers.areAnswersEqual(pendingSave.answers, answersToSave)
    ) {
      return pendingSave.saving;
    }

    if (!hasUnsavedAnswers) {
      return Promise.resolve();
    }

    const previousSaving = pendingSave?.saving.catch(() => undefined);
    const saving = Promise.resolve(previousSaving).then(async () => {
      await sendAnswers(answersToSave);
    });

    const save = { answers: answersToSave, saving };

    pendingSaveRef.current = save;

    return saving.finally(() => {
      if (pendingSaveRef.current === save) {
        pendingSaveRef.current = undefined;
      }
    });
  }, [sendAnswers, stateRef]);

  const openStep = useCallback(
    (stepId: string) => {
      saveAnswers()
        .then(() =>
          dispatch({
            type: 'step.opened',
            fromStepId: currentStepId,
            toStepId: stepId,
          }),
        )
        .catch(() => undefined);
    },
    [currentStepId, saveAnswers, dispatch],
  );

  const reloadExperience = useCallback(async () => {
    await refetchExperience();
  }, [refetchExperience]);

  const reloadStep = useCallback(async () => {
    await refetchStep();
  }, [refetchStep]);

  const generateContent = useCallback(async () => {
    await refetchContent();
  }, [refetchContent]);

  const setAnswer = useCallback(
    (widgetId: string, value: TAnswer) => {
      dispatch({ type: 'answer.set', widgetId, value });
    },
    [dispatch],
  );

  const recordMessages = useCallback(
    (messages: TMessageInfo[]) => {
      queryClient.setQueryData<TGetExperienceResponsePayload>(
        experienceKey,
        (cached) => cached && helpers.appendMessages(cached, messages),
      );
    },
    [queryClient, experienceKey],
  );

  useEffect(() => {
    if (loadedStep) {
      dispatch({ type: 'step.loaded', step: loadedStep });
    }
  }, [loadedStep, dispatch]);

  const value = useMemo<TExperienceContextValue>(
    () => ({
      experienceId,
      experience,
      isLoadingExperience,
      experienceError,
      isConsentRequired,
      isAcceptingConsent,
      acceptConsentError,
      mainSteps,
      currentStepId,
      activeMainStepId,
      previousStepId,
      nextStepId,
      completedStepIds,
      isSideStep,
      isLastStep,
      isStepBusy,
      currentStep,
      isLoadingStep,
      stepError,
      isGeneratingContent,
      generateContentError,
      isSavingAnswers,
      saveAnswersError,
      reloadExperience,
      acceptConsent,
      openStep,
      reloadStep,
      generateContent,
      saveAnswers,
      recordMessages,
    }),
    [
      experienceId,
      experience,
      isLoadingExperience,
      experienceError,
      isConsentRequired,
      isAcceptingConsent,
      acceptConsentError,
      mainSteps,
      currentStepId,
      activeMainStepId,
      previousStepId,
      nextStepId,
      completedStepIds,
      isSideStep,
      isLastStep,
      isStepBusy,
      currentStep,
      isLoadingStep,
      stepError,
      isGeneratingContent,
      generateContentError,
      isSavingAnswers,
      saveAnswersError,
      reloadExperience,
      acceptConsent,
      openStep,
      reloadStep,
      generateContent,
      saveAnswers,
      recordMessages,
    ],
  );

  const answersValue = useMemo<TExperienceAnswersContextValue>(
    () => ({ answers, canAnswer, setAnswer }),
    [answers, canAnswer, setAnswer],
  );

  return (
    <ExperienceContext.Provider value={value}>
      <ExperienceAnswersContext.Provider value={answersValue}>
        {children}
      </ExperienceAnswersContext.Provider>
    </ExperienceContext.Provider>
  );
};

export const ExperienceProvider = (props: TExperienceProviderProps) => (
  <ExperienceContextProvider key={props.experienceId} {...props} />
);
